import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import ComboFilter from "../components/ComboFilter";

interface ExperimentRow {
  id: string;
  name: string;
  status: number;
  owner: string | null;
  traffic_per_flight: number | null;
  start_time: string | null;
  end_time: string | null;
  set_id: string | null;
  result_count: number;
}

// GlobalExperiment#status — Rails HasStatus concern (archived=2 is soft-deleted, excluded below).
const STATUS_LABELS: Record<number, string> = {
  0: "Paused",
  1: "Active",
  2: "Archived",
  3: "Concluded",
};

const STATUS_MODIFIER: Record<number, string> = {
  0: "paused",
  1: "active",
  2: "archived",
  3: "concluded",
};

const EXPERIMENTS_SQL = `
  SELECT
    CAST(e.id AS STRING) AS id,
    e.name,
    e.status,
    COALESCE(NULLIF(e.owner_team, ''), u.name) AS owner,
    e.traffic_per_flight,
    FORMAT_TIMESTAMP('%Y-%m-%dT%H:%M:%SZ', e.start_time) AS start_time,
    FORMAT_TIMESTAMP('%Y-%m-%dT%H:%M:%SZ', e.end_time) AS end_time,
    CAST(e.global_experiment_set_id AS STRING) AS set_id,
    (
      SELECT COUNT(*)
      FROM \`martin-test-datalab.postgres.global_experiment_results\` r
      WHERE r.global_experiment_id = e.id
    ) AS result_count
  FROM \`martin-test-datalab.postgres.global_experiments\` e
  LEFT JOIN \`martin-test-datalab.postgres.users\` u ON e.owner_id = u.id
  WHERE e.status != 2
  ORDER BY
    CASE e.status WHEN 1 THEN 0 WHEN 0 THEN 1 ELSE 2 END,
    e.start_time DESC
`;

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

function formatDate(value: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return dateFmt.format(d);
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start && !end) return "–";
  return `${formatDate(start)} → ${formatDate(end)}`;
}

function formatTraffic(value: number | null): string {
  if (value == null) return "–";
  return `${Math.round(value * 100)}%`;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function ExperimentListPage() {
  const { isAuthenticated, requiresAuth } = useAuth();
  const [rows, setRows] = useState<ExperimentRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");

  useEffect(() => {
    if (requiresAuth && !isAuthenticated) return;
    setIsLoading(true);
    setError(null);

    runBigQueryQuery(EXPERIMENTS_SQL)
      .then(results => setRows(results as unknown as ExperimentRow[]))
      .catch(err => setError(String(err)))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated, requiresAuth]);

  if (isLoading) {
    return <div className="entity-list__loading">Loading…</div>;
  }

  if (error) {
    return (
      <div className="entity-list__error">
        <p>Something went wrong loading experiments. Please try refreshing the page.</p>
        <p className="entity-list__error-detail">{error}</p>
      </div>
    );
  }

  const filterLower = filter.toLowerCase();

  const statusNames = [...new Set(rows.map(r => STATUS_LABELS[r.status]).filter(Boolean))];
  const ownerNames = [...new Set(rows.map(r => r.owner).filter(Boolean) as string[])].sort();

  const visibleRows = rows.filter(r => {
    if (filterLower && !r.name.toLowerCase().includes(filterLower)) return false;
    if (statusFilter && STATUS_LABELS[r.status] !== statusFilter) return false;
    if (ownerFilter && r.owner !== ownerFilter) return false;
    return true;
  });

  const activeCount = visibleRows.filter(r => r.status === 1).length;
  const pausedCount = visibleRows.filter(r => r.status === 0).length;
  const concludedCount = visibleRows.filter(r => r.status === 3).length;

  return (
    <>
      {/* Summary KPIs — updates with filters */}
      <div className="audience-kpi-strip entity-list__summary-strip">
        <div className="audience-kpi-strip__item">
          <div className="entity-detail__kpi-label">Experiments</div>
          <div className="entity-detail__kpi-value">{visibleRows.length}</div>
          {rows.length !== visibleRows.length && (
            <div className="entity-detail__kpi-change" style={{ color: "#8c8b7c" }}>
              of {rows.length}
            </div>
          )}
        </div>
        <div className="audience-kpi-strip__item">
          <div className="entity-detail__kpi-label">Active</div>
          <div className="entity-detail__kpi-value">{activeCount}</div>
        </div>
        <div className="audience-kpi-strip__item">
          <div className="entity-detail__kpi-label">Paused</div>
          <div className="entity-detail__kpi-value">{pausedCount}</div>
        </div>
        <div className="audience-kpi-strip__item">
          <div className="entity-detail__kpi-label">Concluded</div>
          <div className="entity-detail__kpi-value">{concludedCount}</div>
        </div>
      </div>

      <div className="entity-list">
        <table className="entity-list__table">
          <caption className="entity-list__table-caption">
            <div className="entity-list__table-caption-inner">
              <div>
                <h1 className="entity-list__title">
                  Experiments
                  <span className="experiment-beta" title="Exploratory draft — work in progress, not final">
                    Beta
                  </span>
                </h1>
                <p className="entity-list__subtitle">
                  Global Experiment Platform — active experiments first · exploratory draft, not final
                </p>
              </div>
              <div className="entity-list__filters">
                <input
                  className="entity-list__filter"
                  type="text"
                  placeholder="Filter…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
                <ComboFilter
                  placeholder="Status…"
                  options={statusNames}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
                <ComboFilter placeholder="Owner…" options={ownerNames} value={ownerFilter} onChange={setOwnerFilter} />
              </div>
            </div>
          </caption>
          <thead>
            <tr>
              <th>Experiment</th>
              <th>Status</th>
              <th>Owner</th>
              <th className="entity-list__col--right">Traffic / flight</th>
              <th>Period</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(row => (
              <tr key={row.id} className="entity-list__row">
                <td className="entity-list__col--name" title={row.id}>
                  <Link to={`/experiments/${row.id}`} className="entity-list__link">
                    {row.name?.trim() || `Experiment ${row.id}`}
                  </Link>
                  {row.result_count > 0 && (
                    <span className="experiment-results-badge" title={`${row.result_count} analysis run(s)`}>
                      {row.result_count} result{row.result_count === 1 ? "" : "s"}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`experiment-status experiment-status--${STATUS_MODIFIER[row.status] ?? "unknown"}`}>
                    {STATUS_LABELS[row.status] ?? `Status ${row.status}`}
                  </span>
                </td>
                <td className="entity-list__col--audiences">{row.owner ? titleCase(row.owner) : "–"}</td>
                <td className="entity-list__col--right entity-list__col--revenue">
                  {formatTraffic(row.traffic_per_flight)}
                </td>
                <td className="entity-list__col--audiences">{formatPeriod(row.start_time, row.end_time)}</td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={5} className="entity-list__empty">
                  No experiments found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
